import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate, today } from "@/lib/api";
import { Field } from "@/components/common";

export const HEADS = ["cgst", "sgst", "igst"];
export const monthLabel = (m) => (m ? new Date(+m.slice(0, 4), +m.slice(5, 7) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" }) : "");
const nextMonth = (m, step = 1) => { const n = +m.slice(0, 4) * 12 + (+m.slice(5, 7) - 1) + step; return `${Math.floor(n / 12)}-${String((n % 12) + 1).padStart(2, "0")}`; };
const STATUS_CLS = { Cleared: "bg-emerald-50 text-emerald-700 border-emerald-200", Due: "bg-amber-50 text-amber-700 border-amber-200",
  Overdue: "bg-rose-50 text-rose-700 border-rose-200", Running: "bg-sky-50 text-sky-700 border-sky-200", Nil: "bg-muted text-muted-foreground" };

export function StatusPill({ s, testid }) {
  return <span data-testid={testid} className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLS[s] || ""}`}>{s === "Cleared" ? "Cleared" : s === "Running" ? "Chal raha mahina" : s === "Nil" ? "Nil" : s === "Overdue" ? "Overdue" : "Balance Due"}</span>;
}

function HeadInputs({ v, set, prefix }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {HEADS.map((h) => <Field key={h} label={h.toUpperCase()}><Input type="number" min="0" data-testid={`${prefix}-${h}`} value={v[h] ?? ""} onChange={(e) => set({ ...v, [h]: e.target.value })} /></Field>)}
    </div>
  );
}

export function OpeningDialog({ open, onClose, opening, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => { if (open) { setF({ month: opening?.month || "", payable: { ...(opening?.payable || {}) }, itc: { ...(opening?.itc || {}) } }); } }, [open, opening]);
  if (!f) return null;
  const save = async () => {
    try { await api.put("/gst-ledger/opening", f); toast.success("Opening balance saved"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card" data-testid="gst-opening-dialog">
        <DialogHeader><DialogTitle>GST Opening Balance</DialogTitle>
          <DialogDescription>Software shuru karne se pehle ka GST hisaab. Chune hue mahine ke baad se software khud hisaab lagayega.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <Field label="Ye opening balance kis mahine ki liability hai? *">
            <Input type="month" max={nextMonth(today().slice(0, 7), -1)} data-testid="gst-opening-month" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} />
            {f.month && <p className="mt-1 text-xs text-muted-foreground" data-testid="gst-opening-hint">Opening Balance for {monthLabel(f.month)} liability · bharna {monthLabel(nextMonth(f.month))} me (due 20 {monthLabel(nextMonth(f.month))})</p>}
          </Field>
          <div><p className="mb-1 text-sm font-semibold">Pending GST jo bharna baaki hai</p><HeadInputs v={f.payable} set={(payable) => setF({ ...f, payable })} prefix="gst-opening-payable" /></div>
          <div><p className="mb-1 text-sm font-semibold">ITC credit jo bacha hua hai (agle mahine me lagega)</p><HeadInputs v={f.itc} set={(itc) => setF({ ...f, itc })} prefix="gst-opening-itc" /></div>
        </div>
        <DialogFooter><Button data-testid="gst-opening-save" disabled={!f.month} onClick={save}>Save Opening Balance</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ChallanDialog({ month, onClose, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (month) { setF({ date: today(), ref: "", note: "", interest: "", late_fee: "", split: Object.fromEntries(HEADS.map((h) => [h, Math.max(month.balance[h], 0) || ""])) }); }
  }, [month]);
  if (!month || !f) return null;
  const save = async () => {
    try {
      await api.post("/gst-ledger/challans", { month: month.month, date: f.date, ref: f.ref, note: f.note, interest: Number(f.interest || 0), late_fee: Number(f.late_fee || 0), ...Object.fromEntries(HEADS.map((h) => [h, Number(f.split[h] || 0)])) });
      toast.success("Challan recorded"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!month} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card" data-testid="gst-challan-dialog">
        <DialogHeader><DialogTitle>Challan — {monthLabel(month.month)} liability</DialogTitle>
          <DialogDescription>Bank se bhara challan Bank &amp; Reconciliation me "GST Challan Payment" se lagayein. Yahan sirf cash / bahar se bhare challan darj karein.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Challan Date *"><Input type="date" data-testid="gst-challan-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
            <Field label="CPIN / CIN"><Input data-testid="gst-challan-ref" value={f.ref} onChange={(e) => setF({ ...f, ref: e.target.value })} /></Field>
          </div>
          <HeadInputs v={f.split} set={(split) => setF({ ...f, split })} prefix="gst-challan" />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Interest (byaj)"><Input type="number" min="0" data-testid="gst-challan-interest" value={f.interest} onChange={(e) => setF({ ...f, interest: e.target.value })} /></Field>
            <Field label="Late Fee"><Input type="number" min="0" data-testid="gst-challan-late-fee" value={f.late_fee} onChange={(e) => setF({ ...f, late_fee: e.target.value })} /></Field>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="gst-challan-penalty-hint">Der se bharne par challan me jo Interest / Late Fee laga, wahi rakam yahan khud likhein.</p>
        </div>
        <DialogFooter><Button data-testid="gst-challan-save" onClick={save}>Save Challan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PrevMonthBox({ r }) {
  if (!r) return null;
  const label = monthLabel(r.month).split(" ")[0];
  return (
    <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-5" data-testid="gst-prev-month-box">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Pichla mahina · bharna {monthLabel(r.payment_month)} me (due {fmtDate(r.due_date)})</div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-base font-semibold">
        <span data-testid="gst-prev-payable">Previous Month ({label}) Total GST Payable = {inr(r.total_liability, 2)}</span>
        <span className="text-muted-foreground">|</span>
        <span data-testid="gst-prev-paid">{r.challans.length ? `Paid via Challan on ${fmtDate(r.paid_on)} (${inr(r.total_paid, 2)})` : "Challan not paid yet"}</span>
        <span className="text-muted-foreground">|</span>
        <span data-testid="gst-prev-balance" className={r.total_balance > 0.5 ? "text-rose-600" : "text-emerald-600"}>{r.total_balance > 0.5 ? `Balance Due ${inr(r.total_balance, 2)}` : "Cleared"}</span>
      </div>
    </div>
  );
}
